<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import TalkviaCalendarIntegrationAPI from 'dashboard/api/talkviaCalendarIntegration';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const route = useRoute();
const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const isLoading = ref(false);
const isSaving = ref(false);
const isConnecting = ref(false);
const integration = ref(null);

const copy = {
  loading: 'Carregando Google Agenda...',
  title: 'Google Agenda',
  description: 'Sincronize eventos da agenda da Talkvia com o Google Agenda.',
  connected: 'Conta Google conectada.',
  disconnected: 'Conecte uma conta Google para habilitar sincronização.',
  reconnect: 'Reconectar Google',
  connect: 'Conectar Google',
  active: 'Ativar Google Agenda',
  calendarId: 'ID do calendário',
  calendarIdPlaceholder: 'primary ou ID do calendário Google',
  webhookUrl: 'Webhook / sistema externo',
  webhookUrlPlaceholder: 'https://...',
  syncDirection: 'Direção da sincronização',
  save: 'Salvar integração',
  loadFailed: 'Não foi possível carregar a integração do Google Agenda.',
  saveSuccess: 'Integração salva.',
  saveFailed: 'Não foi possível salvar a integração.',
  authorizationFailed: 'Não foi possível iniciar a conexão com o Google.',
  googleConnected: 'Google Agenda conectado.',
  googleConnectionFailed: 'Não foi possível conectar o Google Agenda.',
};

const form = reactive({
  active: false,
  calendar_id: '',
  webhook_url: '',
  sync_direction: 'two_way',
});

const syncDirectionOptions = [
  { value: 'talkvia_to_external', label: 'Talkvia para externo' },
  { value: 'external_to_talkvia', label: 'Externo para Talkvia' },
  { value: 'two_way', label: 'Duas vias' },
];

const hasGoogleCredentials = computed(() =>
  Boolean(integration.value?.has_credentials)
);

const returnTo = computed(() =>
  frontendURL(
    `accounts/${accountId.value}/settings/integrations/google_calendar`
  )
);

const loadIntegration = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaCalendarIntegrationAPI.get();
    integration.value = data;
    Object.assign(form, {
      active: Boolean(data?.active),
      calendar_id: data?.calendar_id || '',
      webhook_url: data?.webhook_url || '',
      sync_direction: data?.sync_direction || 'two_way',
    });
  } catch {
    useAlert(copy.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const saveIntegration = async () => {
  isSaving.value = true;
  try {
    const { data } = await TalkviaCalendarIntegrationAPI.update(form);
    integration.value = data;
    useAlert(copy.saveSuccess);
  } catch {
    useAlert(copy.saveFailed, { variant: 'error' });
  } finally {
    isSaving.value = false;
  }
};

const connectGoogleCalendar = async () => {
  isConnecting.value = true;
  try {
    const {
      data: { url },
    } = await TalkviaCalendarIntegrationAPI.generateAuthorization({
      return_to: returnTo.value,
    });
    window.location.href = url;
  } catch {
    useAlert(copy.authorizationFailed, { variant: 'error' });
  } finally {
    isConnecting.value = false;
  }
};

onMounted(() => {
  if (route.query.google_calendar === 'connected') {
    useAlert(copy.googleConnected);
  } else if (route.query.google_calendar === 'failed') {
    useAlert(copy.googleConnectionFailed, { variant: 'error' });
  }

  loadIntegration();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :loading-message="copy.loading">
    <template #header>
      <BaseSettingsHeader :title="copy.title" :description="copy.description" />
    </template>

    <template #body>
      <form
        class="max-w-3xl rounded-xl border border-n-weak bg-n-card p-6"
        @submit.prevent="saveIntegration"
      >
        <div
          class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
        >
          <div class="flex items-start gap-4">
            <div
              class="grid size-12 place-content-center rounded-xl border border-n-weak bg-n-alpha-2 text-n-blue-10"
            >
              <span class="i-lucide-calendar-days size-6" />
            </div>
            <div>
              <h3 class="mb-1 text-lg font-semibold text-n-slate-12">
                {{ copy.title }}
              </h3>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ hasGoogleCredentials ? copy.connected : copy.disconnected }}
              </p>
            </div>
          </div>

          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isConnecting"
            @click="connectGoogleCalendar"
          >
            <span class="i-lucide-link size-4" />
            {{ hasGoogleCredentials ? copy.reconnect : copy.connect }}
          </button>
        </div>

        <div class="grid gap-5">
          <label
            class="flex items-center gap-3 text-sm font-medium text-n-slate-11"
          >
            <input
              v-model="form.active"
              type="checkbox"
              class="size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            />
            {{ copy.active }}
          </label>

          <label class="grid gap-1.5 text-sm font-medium text-n-slate-11">
            {{ copy.calendarId }}
            <input
              v-model="form.calendar_id"
              class="h-11 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              :placeholder="copy.calendarIdPlaceholder"
            />
          </label>

          <label class="grid gap-1.5 text-sm font-medium text-n-slate-11">
            {{ copy.webhookUrl }}
            <input
              v-model="form.webhook_url"
              type="url"
              class="h-11 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              :placeholder="copy.webhookUrlPlaceholder"
            />
          </label>

          <label class="grid gap-1.5 text-sm font-medium text-n-slate-11">
            {{ copy.syncDirection }}
            <select
              v-model="form.sync_direction"
              class="google-calendar-select h-11 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 px-3 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            >
              <option
                v-for="option in syncDirectionOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>

        <div class="mt-6 flex justify-end">
          <button
            type="submit"
            class="inline-flex h-10 items-center justify-center rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isSaving"
          >
            {{ copy.save }}
          </button>
        </div>
      </form>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.google-calendar-select {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' width='32' height='24' viewBox='0 0 32 24'><polygon points='0,0 32,0 16,24' style='fill: rgb%28110, 111, 115%29'></polygon></svg>");
  background-origin: content-box;
  background-position: right -1.25rem center;
  background-repeat: no-repeat;
  background-size: 9px 6px;
}

.google-calendar-select::-ms-expand {
  display: none;
}
</style>
